import React, { useCallback } from 'react';
import PaymentBenefitSection from '@wconcept/wconcept-ui-renewal/src/components/organisms/_section/PaymentBenefitSection';
import PaymentBenefitItem from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/PaymentBenefitItem';
import {
  PromotionNotice,
  PromotionPaymentBenefitModule,
} from '@wconcept/wconcept-common/models/renewal/promotion';
import useLinkTo from '@hooks/useLinkTo';
import useGa4Event from '@hooks/useGa4Event';
import { type PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';

interface PaymentBenefitModuleProps {
  bgType: string;
  visualTokens: PromotionModuleVisualTokens;
  isDesktop: boolean;
  paymentBenefitData: PromotionPaymentBenefitModule[];
  noticeData: PromotionNotice[];
  mainTitle: string;
  subTitle: string;
}

export default function PaymentBenefitModule({
  bgType,
  visualTokens,
  isDesktop,
  paymentBenefitData,
  noticeData,
  mainTitle,
  subTitle,
}: PaymentBenefitModuleProps) {
  const { linkTo } = useLinkTo();
  const { sendGa4BannerClick, sendGa4ButtonClick } = useGa4Event();
  const onClickNoticeToggle = useCallback(
    (active: boolean) => {
      sendGa4ButtonClick({
        area: 'promotion_paybenefit_notice_button',
        text: `유의사항 ${active ? '보기' : '닫기'}`,
      });
    },
    [sendGa4ButtonClick]
  );

  // \n을 <br>로 변환하고 <br>을 배열로 반환
  const getNoticeList = useCallback(
    () =>
      noticeData
        ?.map(v => v.text.replace(/\n/g, '<br>').split('<br>'))
        ?.flat() || [],
    [noticeData]
  );

  const { presetBgType, customColorsFull } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'full',
  });

  const sectionColorType = customColorsFull ?? presetBgType;

  if (!paymentBenefitData || paymentBenefitData?.length < 2) return null;

  return (
    <PaymentBenefitSection
      title={mainTitle}
      subTitle={subTitle}
      colorType={sectionColorType}
      benefitItems={paymentBenefitData.slice(0, 12)?.map((v, index) => (
        <PaymentBenefitItem
          key={v.moduleId}
          colorType={sectionColorType === 'black' ? 'black' : undefined}
          bgColor={
            typeof sectionColorType === 'object'
              ? sectionColorType.pointColor
              : undefined
          }
          textColor={
            typeof sectionColorType === 'object'
              ? sectionColorType.emphasisTextColor
              : undefined
          }
          imgSrc={v.emblemImageUrl}
          cardName={v.emblemTitle}
          desc={v.paymentCondition}
          amount={v.benefit}
          onClick={() => {
            let issueCd;
            let issueNm;
            if (v.landingUrl) {
              if (v.landingUrl.includes('/event')) {
                issueCd = v.landingUrl.split('/').pop();
                issueNm = '';
              }
            }

            sendGa4BannerClick({
              area: 'promotion_paybenefit_banner',
              page: 'promotion',
              text: v.emblemTitle,
              listNo: index + 1,
              bannerType: 'issue',
              issueCd,
              issueNm,
            });
            if (v.landingUrl) {
              linkTo.push({
                url: v.landingUrl,
              });
            }
          }}
        />
      ))}
      noticeList={
        noticeData && noticeData.length > 0 ? getNoticeList() : undefined
      }
      isDesktop={isDesktop}
      onNoticeToggle={onClickNoticeToggle}
    />
  );
}
